
import { CommonModule } from "@angular/common";
import { HttpClient, HttpClientModule } from "@angular/common/http";
import { DOCUMENT, inject, Injector, NgModule, provideAppInitializer } from "@angular/core";
import { CommonDataService } from "@common/lib/data/common-data.service";
import { provideDialogEventHandler } from "@common/ux/adapt-common-dialog/dialog-event-handler.interface";
import { configureBreeze, NamingConvention, Validator } from "breeze-client";
import { httpClientFetch } from "breeze-client/adapter-angular-httpclient";
import { DataServiceWebApiAdapter } from "breeze-client/adapter-data-service-webapi";
import { ModelLibraryBackingStoreAdapter } from "breeze-client/adapter-model-library-backing-store";
import { UriBuilderJsonAdapter } from "breeze-client/adapter-uri-builder-json";
import { switchMap, tap } from "rxjs/operators";
import { buildInfo } from "../build-info/ADAPT.BuildInfo";
import { BreezeService } from "./breeze.service";
import { EntityPersistentService } from "./entity-persistent.service";

@NgModule({
    declarations: [],
    imports: [
        CommonModule,
        HttpClientModule,
    ],
    providers: [
        {
            provide: BreezeService,
            useFactory: (http: HttpClient, injector: Injector, entityPersistentService: EntityPersistentService) => {
                // breeze configuration that should just be run once
                const angularFetch = httpClientFetch(http);
                const document = injector.get(DOCUMENT);
                configureBreeze({
                    modelLibrary: ModelLibraryBackingStoreAdapter,
                    uriBuilder: UriBuilderJsonAdapter,
                    dataService: DataServiceWebApiAdapter,
                    namingConvention: NamingConvention.camelCase,
                    fetch: (input, init) => {
                        // Keep metadata versioning on every metadata load, including retries.
                        // Requests still pass through Angular's auth and error interceptors.
                        const url = new URL(String(input), document.baseURI);
                        if (url.pathname.endsWith("/Metadata")) {
                            url.searchParams.set("v", buildInfo.sourceHash);
                            input = url.toString();
                        }
                        return angularFetch(input, init);
                    },
                });

                Validator.messageTemplates.required = "Enter a %displayName%";

                return new BreezeService(injector, entityPersistentService);
            },
            deps: [HttpClient, Injector, EntityPersistentService],
        },
        provideAppInitializer(() => {
            const breezeService = inject(BreezeService);
            const commonDataService = inject(CommonDataService);
            const document = inject(DOCUMENT);
            return breezeService.initialisation$.pipe(
                // hide the initial loading spinner once metadata is ready
                tap(() => document.getElementById("initial-spinner")?.remove()),
                switchMap(() => commonDataService.initialisation$),
            );
        }),
        provideDialogEventHandler(EntityPersistentService),
    ],
})
export class AdaptCommonDataModule { }
